/**
 * useTenantStats Hook
 *
 * Fetches and manages tenant statistics.
 *
 * @module features/tenants/hooks/useTenantStats
 */

import { useState, useEffect, useCallback } from 'react';
import type { TenantStats } from '../types';
import { fetchTenantStats } from '../api';

/**
 * Auth fetch function type.
 */
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Return type for useTenantStats hook.
 */
export interface UseTenantStatsResult {
  /** Tenant statistics data */
  stats: TenantStats | null;
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Refresh function to re-fetch stats */
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching and managing tenant statistics.
 *
 * @param authFetch - Authenticated fetch function from useAuth
 * @returns Stats data, loading state, error state, and refresh function
 *
 * @example
 * ```tsx
 * const { stats, loading, error, refresh } = useTenantStats(authFetch);
 *
 * if (loading) return <Loading />;
 * if (error) return <Error message={error} />;
 *
 * return <StatsDisplay stats={stats} onRefresh={refresh} />;
 * ```
 */
export const useTenantStats = (authFetch: AuthFetch): UseTenantStatsResult => {
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTenantStats(authFetch);
      setStats(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tenant stats';
      setError(message);
      console.error('Failed to fetch tenant stats:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { stats, loading, error, refresh };
};
